import React, { useEffect, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Layout, Box } from '@stashinvest/ui';
import {
	ButtonStandard,
	OneString,
	Legal,
	LegalString,
	ButtonLink,
	ButtonChoice,
	Text,
} from '@stashinvest/react-components';
import { postAccount, getRetireApplication } from 'stash/actions/api';
import { setSignUpAccountType, deferAction, trackEvent } from 'stash/actions';
import {
	PERSONAL_BROKERAGE,
	TRADITIONAL_IRA,
	ROTH_IRA,
} from 'stash/constants/accountTypes';
import { selectAccount } from 'stash/selectors/account';
import {
	selectRetireConstants,
	selectFilingStatus,
	selectHouseholdIncomeBracket,
} from 'stash/selectors/investorProfile';
import { selectDateOfBirth } from 'stash/selectors/userProfile';
import { isEmpty } from 'stash/utils/helpers';
import { useModal } from 'stash/utils/hooks';
import { BanjoString } from '@stashinvest/react-banjo';
import { trackAccountSetup_Clicked } from './events';
import InfoSheetIRA from './InfoSheetIRA';

const SignUpRetireIRAChooser = ({ location, completeStep }) => {
	const history = useHistory();
	const dispatch = useDispatch();

	const investAccount = useSelector((state) => selectAccount(state, PERSONAL_BROKERAGE));
	const retireAccount = useSelector(
		(state) => selectAccount(state, TRADITIONAL_IRA) || selectAccount(state, ROTH_IRA)
	);
	const retireConstants = useSelector(selectRetireConstants);
	const dateOfBirth = useSelector(selectDateOfBirth);
	const filingStatus = useSelector(selectFilingStatus);
	const householdIncomeBracket = useSelector(selectHouseholdIncomeBracket);

	const [currentAccountType, setCurrentAccountType] = useState(null);
	const [selectedIRAType, setSelectedIRAType] = useState(null);
	const { isOpen: showIRASheet, open: openIRASheet, close: closeIRASheet } = useModal();
	const isRetireAddOn = /retire-add-on/.test(location.pathname);

	const getRetireConstants = useCallback(() => {
		dispatch(getRetireApplication());
	}, [dispatch]);

	useEffect(() => {
		if (isEmpty(retireConstants)) {
			getRetireConstants();
		}

		if (investAccount?.e_signature_agreed_at || retireAccount?.e_signature_agreed_at) {
			completeStep();
		}
	}, [
		dispatch,
		investAccount,
		retireAccount,
		retireConstants,
		location,
		completeStep,
		getRetireConstants,
		isRetireAddOn,
	]);

	const handleAccountTypeSelection = useCallback(setCurrentAccountType, [
		setCurrentAccountType,
	]);

	const handleSubmit = useCallback(() => {
		if (currentAccountType) {
			const accountData = {
				account_type: currentAccountType,
				filing_status: filingStatus,
				household_income_bracket: householdIncomeBracket,
				date_of_birth: dateOfBirth,
			};

			dispatch(deferAction('postRetireAccount', postAccount({ account: accountData })));
			dispatch(setSignUpAccountType({ retireAccountType: currentAccountType }));

			if (isRetireAddOn) {
				dispatch(
					trackEvent('RetireAddOn', {
						ScreenName: 'IRATypeChooser',
						Action: 'ContinueIRATypeChooser',
						IRAType: currentAccountType,
					})
				);
			} else {
				dispatch(trackEvent('RegistrationStep', { ScreenName: 'ChooseAccountType' }));
			}

			// Unified Portfolio - tracking
			trackAccountSetup_Clicked(currentAccountType);

			completeStep({ canReturn: false });
		}
	}, [
		dispatch,
		currentAccountType,
		isRetireAddOn,
		dateOfBirth,
		filingStatus,
		householdIncomeBracket,
		completeStep,
	]);

	const handleIRASheetOpen = (iraType) => {
		setSelectedIRAType(iraType);
		openIRASheet();
	};

	return (
		<Layout.ContentWrap>
			<div className="mt-layout2XL">
				<OneString className="justify-center" textStyle="headerMedium" color="supporting">
					<BanjoString stringId="information/instruct/openAccount" />
				</OneString>
				<Legal>
					<LegalString className="bodySmallStandard">
						<BanjoString stringId="action/explore/IRACompare" />{' '}
						<ButtonLink variant="primary" onClick={() => handleIRASheetOpen(ROTH_IRA)}>
							Roth IRA
						</ButtonLink>{' '}
						and a{' '}
						<ButtonLink
							variant="primary"
							onClick={() => handleIRASheetOpen(TRADITIONAL_IRA)}
						>
							Traditional IRA
						</ButtonLink>
						{showIRASheet && (
							<InfoSheetIRA iraType={selectedIRAType} setShowSheet={closeIRASheet} />
						)}
					</LegalString>
				</Legal>
				<Box
					mb="s24"
					onClick={() => handleAccountTypeSelection(ROTH_IRA)}
					data-testid="roth-ira-option"
				>
					<ButtonChoice
						isSelected={currentAccountType === ROTH_IRA}
						header={<BanjoString stringId="action/startTask/openRothIRA" />}
						body={<BanjoString stringId="information/sell/rothIRA" />}
					>
						<div className="flex flex-col gap-content2XS">
							<Text tag="p" textStyle="bodyMediumEmphasize">
								<BanjoString stringId="action/startTask/openRothIRA" />
							</Text>
							<Text tag="p" textStyle="bodyMediumStandard">
								{' '}
								<BanjoString stringId="information/sell/rothIRA" />
							</Text>
						</div>
					</ButtonChoice>
				</Box>
				<Box
					mb="s24"
					onClick={() => handleAccountTypeSelection(TRADITIONAL_IRA)}
					data-testid="traditional-ira-option"
				>
					<ButtonChoice isSelected={currentAccountType === TRADITIONAL_IRA}>
						<div className="flex flex-col gap-content2XS">
							<Text tag="p" textStyle="bodyMediumEmphasize">
								<BanjoString stringId="action/startTask/openTraditionalIRA" />
							</Text>
							<Text tag="p" textStyle="bodyMediumStandard">
								<BanjoString stringId="information/sell/traditionalIRA" />
							</Text>
						</div>
					</ButtonChoice>
				</Box>
				<Box mb="s12">
					<ButtonStandard
						size="XL"
						className="mx-auto"
						variant="primary"
						onClick={handleSubmit}
						disabled={!currentAccountType}
					>
						<BanjoString stringId="action/progress/nextScreen" />
					</ButtonStandard>
				</Box>
				<Box>
					<ButtonStandard
						size="XL"
						className="mx-auto"
						variant="tertiary"
						onClick={() => history.push('/invest')}
					>
						<BanjoString stringId="action/escape/skipTemporary" />
					</ButtonStandard>
				</Box>
			</div>
		</Layout.ContentWrap>
	);
};

SignUpRetireIRAChooser.propTypes = {
	investAccount: PropTypes.object,
	retireAccount: PropTypes.object,
	completeStep: PropTypes.string,
	retire_constants: PropTypes.string,
	eligible_account_types: PropTypes.string,
	getRetireConstants: PropTypes.func,
	trackOnPage: PropTypes.func,
	date_of_birth: PropTypes.oneOfType(Date),
	showAge70Error: PropTypes.func,
	onSubmit: PropTypes.func,
	filing_status: PropTypes.string,
	household_income_bracket: PropTypes.string,
	location: PropTypes.object.isRequired,
};

export default SignUpRetireIRAChooser;
